import {POPUP} from "back/commons/widgets/popups";
import {AgEltBoxSelection, IEltBoxSelection, JLDScCommentPending, JLDScComments} from "back/edit/wed/features/boxSel";
import {IWedletModel, IWedSelector, WedModel} from "back/edit/wed/wedCore";
import {WedEditAction, WedMgr} from "back/edit/wed/wedEditor";
import {BoxCommentModel, BoxCommentWedlet, BoxModel} from "back/edit/wed/wedlets/box/box";
import {EWedletEditMode, EWedletEditModeLabel, IElementWedlet, IWedlet, IWedletActionCtx, WEDLET, WedletActionCtx} from "back/edit/wed/wedlets/wedlet";
import {isWedletSingleElt, IWedletSingleElt} from "back/edit/wed/wedlets/wedletSingleElt";
import {REG} from "lib/commons/registry";
import {ISecurityCtxPointer} from "lib/commons/security";
import {DOM, ENodeType, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {IJmlNode, IJmlSubSet} from "lib/commons/xml/jml";
import {XA} from "lib/commons/xml/xAddr";
import {InitSlaveRep, IPublicDoor, Msg, ResetStatesMsg} from "lib/edit/ot/urban";
import {isXmlMsg, IXmlMsg, XmlBatch, XmlDeleteMsg, XmlInsertMsg, XmlStrMsg} from "lib/edit/ot/xmlHouse";
import {CommentHouse} from "lib/edit/ot/xmlTypedHouse";
import {JLastDatas} from "lib/commons/lastDatas";
import {ISkAnnot, ISkAnnotDrawer} from "lib/edit/schema/schemaAnnots";
import {SkSearchCommentAnnot, SkSearchTextAnnot} from "lib/edit/schema/schemaSearch";
import {insertAnnot, removeAnnot} from "back/edit/wed/wedlets/box/boxTags";
import {ACTION} from "lib/commons/actions";
import {HistoEditPoint} from "lib/edit/histoEditPoints";

/**
 * ScComment (schéma XML dédié dans un commentaire).
 */
export class BoxScComment extends HTMLElement implements IElementWedlet, IEltBoxSelection, ISkAnnotDrawer {

	wedlet: BoxCommentWedlet;

	cmtHouse: CommentHouse | null;
	cmtHouseDoor: IPublicDoor;

	protected _threadSrcElt: Element;

	_cmtsCtn: HTMLElement;
	protected _closedCheckbox: HTMLInputElement;

	/** Map des annots par élément source <comment>. */
	protected searchAnnots: Map<Element, SkSearchCommentAnnot[]>;

	configWedletElt(tpl: Element, wedlet: BoxCommentWedlet) {
		this.wedlet = wedlet;
		WEDLET.installSkins(tpl, this.attachShadow(DOMSH.SHADOWDOM_INIT), wedlet, this.localName);
		this.setAttribute("sc-comments", "no");
		this.shadowRoot.addEventListener("copy", (ev: ClipboardEvent) => {
			const sel = DOMSH.findDocumentOrShadowRoot(ev.target as HTMLElement).getSelection();
			if (sel.type === "Range") {
				const rg = sel.getRangeAt(0);
				ev.clipboardData.setData("text/plain", rg.cloneContents().textContent);
			}
			ev.stopImmediatePropagation();
			ev.preventDefault();
		});
		if (wedlet.cmtHouse == null) {
			//création d'un nouveau comment.
			this.drawThread();
		} else {
			wedlet.cmtHouse.then((cmtH) => {
				this.cmtHouse = cmtH;
				this.cmtHouseDoor = cmtH.openPublicDoor();
				this._threadSrcElt = this.cmtHouse.document.documentElement;
				this.drawThread();
				this.cmtHouseDoor.addMsgListener((msg: Msg) => {
					//console.log("cmtMsg:::", msg);
					if (msg.getMeta('$cmAct')) {
						//Msg comment standard
						this._applyXmlMsg(msg);
					} else if (isXmlMsg(msg) || msg.type === ResetStatesMsg.type || msg.type === InitSlaveRep.type) {
						//Autre msg, On redessine tout.
						this.drawThread();
					}
				});
			});
		}
	}

	drawAnnot(annot: ISkAnnot): boolean {
		if (annot instanceof SkSearchCommentAnnot) {
			if (!this.searchAnnots) this.searchAnnots = new Map();
			const eltComment = annot.subAnnot.anchorNode.parentElement;
			if (!eltComment) return false;
			let annots = this.searchAnnots.get(eltComment);
			if (annots) annots.push(annot);
			else {
				annots = [annot];
				this.searchAnnots.set(eltComment, annots);
			}
			this._redrawCmAnnots(eltComment, annots);
			return true;
		} else if (this === this.wedlet.element) {
			//diffad/rem
			insertAnnot(this, annot);
			return true;
		}
		return false;
	}

	eraseAnnot(annot: ISkAnnot): boolean {
		if (annot instanceof SkSearchCommentAnnot && this.searchAnnots) {
			const eltComment = annot.subAnnot.anchorNode.parentElement;
			if (!eltComment) return false;
			const annots = this.searchAnnots.get(eltComment);
			if (annots) {
				if (annots.length === 1 && annots[0] === annot) {
					this.searchAnnots.set(eltComment, null);
					this._redrawCmAnnots(eltComment, null);
					return true;
				} else {
					const idx = annots.indexOf(annot);
					if (idx >= 0) {
						annots.splice(idx, 1);
						this._redrawCmAnnots(eltComment, annots);
						return true;
					}
				}
			}
		} else if (this === this.wedlet.element) {
			//diff add/rem
			return removeAnnot(this, annot);
		}
		return false;
	}

	get threadClosed(): boolean {return this._threadSrcElt && this._threadSrcElt.getAttribute("threadClosed") === 'true'}

	toggleCloseState() {
		const xa = XA.append(XA.fromNode(this._threadSrcElt), "threadClosed");
		if (this.threadClosed) {
			this.cmtHouseDoor.receiveMsg(new XmlStrMsg().init(xa, null).setMeta('$cmAct', 'state'));
		} else {
			this.cmtHouseDoor.receiveMsg(new XmlStrMsg().init(xa, "true").setMeta('$cmAct', 'state'));
		}
	}

	addCmt(text?: string) {
		startEdition(this, this._cmtsCtn.appendChild(<div class="cmt"/>), text);
	}

	getCmt(offs: number): HTMLElement & IScCmtXmlSrc {
		return this._cmtsCtn.children[offs] as HTMLElement & IScCmtXmlSrc;
	}

	editCmt(cmt: HTMLElement & IScCmtXmlSrc, overwriteText?: string) {
		startEdition(this, cmt, overwriteText);
	}

	execAddCm(txt: string, by?: string) {
		if (!this._threadSrcElt) {
			//On est en virtual, création du 1er comment
			const xa = this.wedlet.wedAnchor;
			const root = document.createElementNS("scenari.eu:comment:1.0", "comment");
			root.setAttribute("type", "thread");
			const cmt = root.appendChild(document.createElementNS("scenari.eu:comment:1.0", "comment"));
			if (by) cmt.setAttribute("author", by);
			cmt.setAttribute("creationTime", Date.now().toString(10));
			cmt.appendChild(document.createTextNode(txt || ""));
			const wedMgr = this.wedlet.wedMgr;
			wedMgr.docHolder.newBatch().insertJml(xa, [{'': '!', '=': DOM.escapeComment(DOM.ser(root))}]).doBatch();
			this.wedlet.deleteVirtualWedlet();
			wedMgr.config.histoEditPointsMgr?.addEditEntry(new HistoEditPoint(xa, wedMgr.docHolder.house), null, wedMgr.config.histoEditHolder);
		} else {
			const cmNode = {'': 'comment'} as any;
			if (by) cmNode.author = by;
			cmNode.creationTime = Date.now().toString(10);
			const jml = [cmNode, [txt || ""]];
			const xa = XA.append(XA.fromNode(this._threadSrcElt), DOM.computeOffset(this._threadSrcElt.lastElementChild, -1) + 1);
			this.cmtHouseDoor.receiveMsg(new XmlInsertMsg().init(xa, jml).setMeta('$cmAct', 'cm'));
			const wedMgr = this.wedlet.wedMgr;
			wedMgr.config.histoEditPointsMgr?.addEditEntry(new HistoEditPoint(this.wedlet.wedAnchor, wedMgr.docHolder.house), null, wedMgr.config.histoEditHolder);
		}
	}

	execDelCm(cmtSrc: Element) {
		if (!cmtSrc.nextElementSibling && !cmtSrc.previousElementSibling) {
			//C'est le 1er et dernier, on supprime tout le commentaire
			this.wedlet.wedMgr.docHolder.newBatch().deleteSequence(this.wedlet.wedAnchor, 1).doBatch();
		} else {
			//On suppr ce comment dans le thread.
			this.cmtHouseDoor.receiveMsg(new XmlDeleteMsg().init(XA.fromNode(cmtSrc), 1).setMeta('$cmAct', 'cm'));
		}
		const wedMgr = this.wedlet.wedMgr;
		wedMgr.config.histoEditPointsMgr?.addEditEntry(new HistoEditPoint(this.wedlet.wedAnchor, wedMgr.docHolder.house), null, wedMgr.config.histoEditHolder);
	}

	execUpdtCm(cmtSrc: Element, txt: string, updtBy?: string) {
		const by = cmtSrc.getAttribute("author");
		const create = cmtSrc.getAttribute("creationTime");
		const xa = XA.fromNode(cmtSrc);
		const cmNode = {'': 'comment'} as any;
		if (by) cmNode.author = by;
		if (create) cmNode.creationTime = create;
		if (updtBy) cmNode.updatedBy = updtBy;
		cmNode.updateTime = Date.now().toString(10);
		const jml = [cmNode, [txt || ""]];
		const m = new XmlBatch()
			.add(new XmlDeleteMsg().init(xa, 1))
			.add(new XmlInsertMsg().init(xa, jml))
			.setMeta('$cmAct', 'cm');
		this.cmtHouseDoor.receiveMsg(m);
	}

	protected drawThread() {
		const threadHtml = this.shadowRoot;
		let last: Element;
		while ((last = threadHtml.lastElementChild) && last.localName !== 'style') last.remove();

		const threadBar = threadHtml.appendChild(<div id="cmtsBar"/>);
		this._cmtsCtn = threadHtml.appendChild(<div id="cmts"/>);

		if (this.wedlet.isVirtual()) {
			//thread de comment en création
			const cm = this._cmtsCtn.appendChild(<div class="cmt"/>);
			startEdition(this, cm, this.wedlet.initText);
			return;
		}

		this._closedCheckbox = threadBar.appendChild(<input id="close" type="checkbox" onchange={onToggleState}/>) as HTMLInputElement;
		this.drawState();

		threadBar.appendChild(<span style="flex:1"/>);
		threadBar.appendChild(<button id="addCm" title="Répondre" onclick={addComment}>{JSX.asSvg(() =>
			<svg viewBox="0 0 448 512">
				<path
					d="m 393.22034,234.84746 v 42.30508 c 0,8.72543 -7.13898,15.86441 -15.86441,15.86441 H 261.01695 v 116.33898 c 0,8.72543 -7.13898,15.86441 -15.86441,15.86441 h -42.30508 c -8.72543,0 -15.86441,-7.13898 -15.86441,-15.86441 V 293.01695 H 70.644068 c -8.725424,0 -15.864407,-7.13898 -15.864407,-15.86441 v -42.30508 c 0,-8.72543 7.138983,-15.86441 15.864407,-15.86441 H 186.98305 V 102.64407 c 0,-8.725426 7.13898,-15.864409 15.86441,-15.864409 h 42.30508 c 8.72543,0 15.86441,7.138983 15.86441,15.864409 v 116.33898 h 116.33898 c 8.72543,0 15.86441,7.13898 15.86441,15.86441 z"/>
			</svg>)
		}</button>);

		for (let cm = this._threadSrcElt.firstElementChild; cm; cm = cm.nextElementSibling) {
			this._drawCm(cm);
		}

		this.setEditMode(this.wedlet.editMode);
	}

	setEditMode(mode: EWedletEditMode): void {
		const sr = this.shadowRoot;
		const readOnly = mode !== EWedletEditMode.write;
		DOM.findNext(sr, sr, (n: Node): n is Node => {
			if (n instanceof HTMLInputElement || n instanceof HTMLButtonElement) n.disabled = readOnly;
			return false;
		});
		DOM.setAttr(this, "edit-mode", EWedletEditModeLabel[mode]);
	}

	protected drawState() {
		const stClosed = this.threadClosed;
		this._closedCheckbox.title = stClosed ? "Réactiver ce commentaire" : "Clore ce commentaire";
		this.classList.toggle("closed", stClosed);
		if (this._closedCheckbox.checked !== stClosed) this._closedCheckbox.checked = stClosed;
	}

	_drawCm(cm: Element, insertBefore?: Node) {
		if (cm.localName !== 'comment') return;
		let user = cm.getAttribute("author");
		let date = dateFormatter.format(new Date(Number.parseInt(cm.getAttribute("creationTime"))));
		let meta: HTMLElement;
		if (user) {
			meta = <span class="meta">{`Créé le ${date} par `}
				<c-userref account={user}/></span>
		} else {
			meta = <span class="meta">{`Créé le ${date}`}</span>
		}
		const updt = cm.getAttribute("updateTime");
		if (updt) {
			date = dateFormatter.format(new Date(Number.parseInt(updt)));
			user = cm.getAttribute("updatedBy");
			if (user) {
				meta.append(
					` (modifié le ${date} par `,
					<c-userref account={user}/>,
					`)`
				);
			} else {
				meta.append(` (modifié le ${date})`);
			}
		}
		const cmHtml = this._cmtsCtn.insertBefore(<div class="cmt" ondblclick={editComment}>
			<div class="cmtHead">
				{meta}
				<button class="editCm" onclick={editComment} title="Éditer ce commentaire">
					{JSX.asSvg(() => <svg viewBox="0 0 512 512">
						<path
							d="M497.9 142.1l-46.1 46.1c-4.7 4.7-12.3 4.7-17 0l-111-111c-4.7-4.7-4.7-12.3 0-17l46.1-46.1c18.7-18.7 49.1-18.7 67.9 0l60.1 60.1c18.8 18.7 18.8 49.1 0 67.9zM284.2 99.8L21.6 362.4.4 483.9c-2.9 16.4 11.4 30.6 27.8 27.8l121.5-21.3 262.6-262.6c4.7-4.7 4.7-12.3 0-17l-111-111c-4.8-4.7-12.4-4.7-17.1 0zM124.1 339.9c-5.5-5.5-5.5-14.3 0-19.8l154-154c5.5-5.5 14.3-5.5 19.8 0s5.5 14.3 0 19.8l-154 154c-5.5 5.5-14.3 5.5-19.8 0zM88 424h48v36.3l-64.5 11.3-31.1-31.1L51.7 376H88v48z"/>
					</svg>)}
				</button>
				<button class="delCm" onclick={delComment} title="Supprimer ce commentaire">
					{JSX.asSvg(() => <svg viewBox="0 0 448 512">
						<path
							d="M268 416h24a12 12 0 0 0 12-12V188a12 12 0 0 0-12-12h-24a12 12 0 0 0-12 12v216a12 12 0 0 0 12 12zM432 80h-82.41l-34-56.7A48 48 0 0 0 274.41 0H173.59a48 48 0 0 0-41.16 23.3L98.41 80H16A16 16 0 0 0 0 96v16a16 16 0 0 0 16 16h16v336a48 48 0 0 0 48 48h288a48 48 0 0 0 48-48V128h16a16 16 0 0 0 16-16V96a16 16 0 0 0-16-16zM171.84 50.91A6 6 0 0 1 177 48h94a6 6 0 0 1 5.15 2.91L293.61 80H154.39zM368 464H80V128h288zm-212-48h24a12 12 0 0 0 12-12V188a12 12 0 0 0-12-12h-24a12 12 0 0 0-12 12v216a12 12 0 0 0 12 12z"/>
					</svg>)}
				</button>
			</div>
			<div class="content">{this._getContent(cm.textContent, this.searchAnnots?.get(cm))}</div>
		</div>, insertBefore) as HTMLElement & IScCmtXmlSrc;
		cmHtml.xmlSrc = cm;
	}

	protected _redrawCmAnnots(cm: Element, annots: SkSearchCommentAnnot[] | null) {
		if (this._cmtsCtn) for (const cmHtml of this._cmtsCtn.children) {
			if ((cmHtml as IScCmtXmlSrc).xmlSrc === cm) {
				const htmlElt = cmHtml.querySelector(".content");
				if (htmlElt) {
					htmlElt.textContent = null;
					htmlElt.append(...this._getContent(cm.textContent, annots));
				}
				break;
			}
		}
	}

	protected _getContent(content: string, annots: SkSearchCommentAnnot[] | null): string | Array<string | Node> {
		if (!annots) return content;
		const res: Array<string | Node> = [];
		let idx = 0;
		for (const an of annots) {
			const start = (an.subAnnot as SkSearchTextAnnot).offsetStart;
			const end = start + an.subAnnot.len;
			if (idx < start) res.push(content.substring(idx, start));
			res.push(<mark>{content.substring(start, end)}</mark>);
			idx = end;
		}
		if (idx < content.length) res.push(content.substring(idx));
		return res;
	}

	/** Redraw suite à chgt issu de la house. */
	protected _applyXmlMsg(msg: Msg) {
		//On ne gère que les évènements : chgt état closed, supp d'un comment (entier), add d'un comment (entier).
		const act = msg.getMeta('$cmAct');
		if (act === 'state') {
			//Etat close du thread
			this.drawState();
		} else if (act === 'cm') {
			const delCm = (m: XmlDeleteMsg) => {
				for (let cm = this._cmtsCtn.firstElementChild as HTMLElement & IScCmtXmlSrc; cm;) {
					const next = cm.nextElementSibling as HTMLElement & IScCmtXmlSrc;
					if (!cm.xmlSrc.isConnected) cm.remove();
					cm = next;
				}
			};

			const addCm = (m: XmlInsertMsg) => {
				const cm = XA.findDomLast(m.xa, this.cmtHouse.root) as Element;
				if (cm) {
					let nextHtml: Node;
					const nextCmtSrc = cm.nextElementSibling;
					if (nextCmtSrc) this._cmtsCtn.childNodes.forEach((e: Node) => {
						if ((e as IScCmtXmlSrc).xmlSrc === nextCmtSrc) nextHtml = e;
					});
					this._drawCm(cm, nextHtml);
				}
			};

			if (msg instanceof XmlDeleteMsg) {
				//Suppr d'un comment
				delCm(msg);
			} else if (msg instanceof XmlInsertMsg) {
				//Ajout d'un comment
				addCm(msg);
			} else if (msg instanceof XmlBatch) {
				//Update d'un comment
				delCm(msg.msgs.find((m) => m instanceof XmlDeleteMsg) as XmlDeleteMsg);
				addCm(msg.msgs.find((m) => m instanceof XmlInsertMsg) as XmlInsertMsg);
			}
		} else throw Error("Unknown ScComment act msg: " + act);
	}

	refreshBindValue(val: IJmlNode | string, children?: IJmlSubSet): void {
	}

	/** Appelé en mode remoteOT : la house holder et la commentHouse sont modifiés en // par leur master respectif, la couche slave ne resynch pas. */
	replaceChars(chars: string, msg: IXmlMsg) {}

	get isEditing(): boolean {return this.classList.contains("editing")}

	setEditing(editing: boolean) {
		const wedMgr = this.wedlet.wedMgr;
		if (editing) {
			this.classList.add("editing");
			if (!this._lstn_beforeHide) this._lstn_beforeHide = (wedMgr: WedMgr, close: boolean): undefined | false => {
				if (close) {
					POPUP.showNotifWarning("Veuillez valider ou abandonner votre commentaire.", this);
					this.focus();
					const input = this.shadowRoot.querySelector('textarea');
					if (input) input.focus();
					return false;
				}
			};
			if (!this._lstn_builsLastDatas) this._lstn_builsLastDatas = (wedMgr: WedMgr, lastDatas: JLDScComments) => {
				const textarea = this.shadowRoot.querySelector('textarea');
				if (!textarea || !textarea.value) return;
				const xmlSrc = (textarea.closest('.cmt') as IScCmtXmlSrc).xmlSrc;
				if (xmlSrc && xmlSrc.textContent === textarea.value) return;
				if (!lastDatas.scCmtsPd) lastDatas.scCmtsPd = [];
				lastDatas.scCmtsPd.push({
					xa: this.wedlet.wedAnchor,
					txt: textarea.value,
					offs: xmlSrc ? DOM.computeOffset(xmlSrc) : this.wedlet.isVirtual() ? undefined : -1,
					dt: xmlSrc ? xmlSrc.getAttribute("updateTime") || xmlSrc.getAttribute("creationTime") : undefined
				});
			};
			wedMgr.listeners.on("beforeHide", this._lstn_beforeHide);
			wedMgr.listeners.on("beforeHideAsync", beforeHideAsync);
			wedMgr.listeners.on("buildLastDatas", this._lstn_builsLastDatas);
		} else {
			this.classList.remove("editing");
			wedMgr.listeners.removeListener("beforeHide", this._lstn_beforeHide);
			wedMgr.listeners.removeListener("beforeHideAsync", beforeHideAsync);
			wedMgr.listeners.removeListener("buildLastDatas", this._lstn_builsLastDatas);
		}
	}

	_lstn_beforeHide: (wedMgr: WedMgr, close: boolean) => undefined | false;
	_lstn_builsLastDatas: (wedMgr: WedMgr, lastDatas: JLastDatas) => void;
}

AgEltBoxSelection(BoxScComment, {selMode: "box", actionsLists: ['actions:wed:box-sccomment', 'actions:wed:box']});

type IScCmtXmlSrc = { xmlSrc?: Element, txtArea?: HTMLTextAreaElement }

function beforeHideAsync(wedMgr: WedMgr, close: boolean): Promise<undefined | false> {return Promise.resolve(close ? false : undefined) as Promise<undefined | false>}

const dateFormatter = new Intl.DateTimeFormat('fr', {
	year: 'numeric', month: 'numeric', day: 'numeric',
	hour: 'numeric', minute: 'numeric', second: 'numeric'
});

function onToggleState(this: HTMLInputElement, ev: Event) {
	(DOMSH.findHost(this) as BoxScComment).toggleCloseState();
}

function addComment(this: HTMLElement, ev: Event) {
	(DOMSH.findHost(this) as BoxScComment).addCmt();
}

function delComment(this: HTMLElement, ev: Event) {
	(DOMSH.findHost(this) as BoxScComment).execDelCm((this.closest('.cmt') as IScCmtXmlSrc).xmlSrc);
}

function editComment(this: HTMLElement, ev: Event) {
	const cmt = this.closest('.cmt') as HTMLElement & IScCmtXmlSrc;
	startEdition(DOMSH.findHost(this) as BoxScComment, cmt);
}

function startEdition(boxCmts: BoxScComment, cmt: HTMLElement & IScCmtXmlSrc, overwrite?: string): void {
	if (!boxCmts || boxCmts.isEditing || boxCmts.threadClosed || !WEDLET.isWritableWedlet(boxCmts.wedlet)) return;

	const startHeight = cmt.xmlSrc ? Math.max(cmt.querySelector<HTMLElement>(':scope>.content').offsetHeight, 50) : 50;

	function valid(this: void, ev: Event) {
		const val = cmt.txtArea.value;
		if (val === (cmt.xmlSrc ? cmt.xmlSrc.textContent : "")) {
			cancel(ev);
			return;
		}
		boxCmts.setEditing(false);
		const secCtx = (boxCmts.wedlet.wedMgr.reg.env as ISecurityCtxPointer).securityCtx;
		const user = secCtx ? secCtx.account : null;
		if (cmt.xmlSrc) {
			boxCmts.execUpdtCm(cmt.xmlSrc, val, user);
		} else {
			cmt.remove();
			boxCmts.execAddCm(val, user);
		}
		boxCmts.focus();
	}

	function cancel(this: void, ev: Event) {
		boxCmts.setEditing(false);
		if (cmt.xmlSrc) {
			//abandon de l'update, on redessine ce comment
			boxCmts._drawCm(cmt.xmlSrc, cmt);
			boxCmts.setEditMode(boxCmts.wedlet.editMode);
			cmt.remove();
		} else if (boxCmts.wedlet.isVirtual()) {
			//abandon création d'un thread de comment
			const parent = boxCmts.wedlet.wedParent;
			boxCmts.wedlet.deleteVirtualWedlet();
			if (isWedletSingleElt(parent) && parent.element.onChildWedletsChange) parent.element.onChildWedletsChange();
		} else {
			//abandon création d'un comment dans un thread
			cmt.remove();
		}
		boxCmts.focus();
	}

	boxCmts.setEditing(true);
	cmt.classList.add("editing");
	cmt.textContent = null;
	cmt.txtArea = <textarea style={startHeight ? `height:${startHeight}px` : 'height:3em'}>{cmt.xmlSrc ? cmt.xmlSrc.textContent : ""}</textarea> as HTMLTextAreaElement;
	cmt.appendChild(<div class="cmEdit">
		{cmt.txtArea}
		<div class="editBar">
			<c-button class="edit valid" onclick={valid} label="Valider" ui-context="dialog"/>
			<c-button class="edit cancel" onclick={cancel} label="Annuler" ui-context="dialog"/>
		</div>
	</div>);
	const txtarea = cmt.querySelector('textarea');
	cmt.onkeydown = async (ev: KeyboardEvent) => {
		ev.stopImmediatePropagation(); //on bloque les comportements du wed au dessus.
		if (ACTION.isAccelPressed(ev) && ev.key === "s") {
			ev.preventDefault(); //bloque l'enreg par le navigateur de la page web
			valid(ev);
		} else if (!ACTION.isAnyControlPressed(ev) && ev.key === "Escape") {
			if (cmt.txtArea.value === (cmt.xmlSrc ? cmt.xmlSrc.textContent : "") || await POPUP.confirm("Confirmez-vous l'abandon de vos modifications de ce commentaire ?", cmt)) {
				cancel(ev);
			}
		}
	};
	//On reste sur les actions standard dans le textarea.
	txtarea.onkeypress = txtarea.oncontextmenu = txtarea.oncopy = txtarea.oncut = txtarea.onpaste = (ev: Event) => ev.stopImmediatePropagation();

	//Focus en async dans le cas de l'insertion d'un comment, car pas encore dans l'arbre
	Promise.resolve().then(() => {
		boxCmts.focus(); //on focus d'abord le boxWidget (gestion du boxSel), puis on y entre
		txtarea.focus();
		if (overwrite) txtarea.value = overwrite; //FIXME comment permettre ctrl+Z et retrouver la val originale.
	});
}


REG.reg.registerSkin('box-sccomment', 1, /* language=CSS */ `
	:host {
		color: var(--edit-cmt-color);
		display: flex;
		min-height: 0;
		min-width: 0;
		margin: .5em;
		box-shadow: .1em .1em .5em .1em var(--edit-cmt-shadow-color);
		clear: both;
		font-size: 1rem;
	}

	::selection {
		color: currentColor;
		background: var(--edit-seltext-bgcolor);
	}

	:host(:focus) {
		outline: var(--edit-box-focus);
	}

	:host(.closed) {
		text-decoration: line-through;
	}

	:host(.closed) button,
	:host(.editing) button:not(.edit),
	:host(.editing) input {
		display: none;
	}

	:host([edit-mode=rs]) {
		display: none;
	}

  :focus-visible {
		outline: var(--focus-outline);
	}

	#cmtsBar {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		align-items: center;
		font-size: var(--label-size);
		background: linear-gradient(var(--edit-cmt-bgcolor), var(--edit-cmt-bgcolor)), var(--edit-cmt-bgcolor);/*double l'opacité == .cmtHead*/
		border-inline-end: 1px solid var(--edit-cmt-border-color);
		align-content: center;
		width: 2em;
	}

	#cmts {
		flex: 1;
		background-color: var(--edit-cmt-bgcolor);
	}

	.cmt:not(:first-child) {
		border-top: 1px solid var(--edit-cmt-border-color);
	}

	.cmtHead {
		display: flex;
		min-height: 0;
		min-width: 0;
		align-items: center;
		background-color: var(--edit-cmt-bgcolor);
		font-size: var(--label-size);
		font-style: italic;
		fill: var(--edit-cmt-color);
	  padding: .2em;
	}

	.content {
		white-space: pre-wrap;
		padding: .2em;
	  min-height: 1em;
		min-height: 1 lh;
		user-select: text;
	}

	.meta {
		flex: 1;
	}

	c-userref {
		display: inline-flex;
	}

	.editBar {
		display: flex;
		min-height: 0;
		min-width: 0;
		justify-content: flex-end;
	}

	button {
		-webkit-appearance: none;
		border: 1px solid var(--edit-cmt-border-color);
		border-radius: .2em;
		padding: .2em;
		background-color: var(--edit-cmt-bgcolor);
		width: 1.5em;
		height: 1.5em;
		font-size: var(--label-size);
		margin: 2px;
		cursor: pointer;
		fill: var(--edit-cmt-color);
		opacity: .8;
	}

	button:disabled {
		opacity: .4;
		cursor: auto;
	}

	button:hover:not(:disabled) {
		filter: brightness(1.2);
	}

	.cm.editing {
		position: relative;
	}

	textarea {
		width: 100%;
		resize: vertical;
		box-sizing: border-box;
		border: none;
		border-bottom: 1px solid var(--edit-cmt-border-color);
		padding: 1px;
		font-size: 1em;
		background: var(--edit-bgcolor);
		color: var(--edit-color);
	}

	.cmEdit {
		/*position: absolute;*/
		/*top: 0;*/
		box-shadow: .1em .1em .5em .1em var(--edit-cmt-shadow-color);
		background-color: var(--edit-cmt-bgcolor);
	}

	c-button {
		margin: .2em .5em;
	}
	
	#close {
			cursor: pointer;
  }

  ::selection {
		color: currentColor;
	  background-color: var(--edit-seltext-bgcolor);
  }
`);

window.customElements.define("box-sccomment", BoxScComment);


export function initLastDatasScComments(wedMgr: WedMgr, comments: JLDScCommentPending[]) {
	for (const cmtPending of comments) {
		//console.log("reinject comment::", cmtPending);
		if (cmtPending.offs !== undefined) {
			const cmtWedlet = WEDLET.findWedlet(wedMgr.rootWedlet, cmtPending.xa, {forceFetch: true});
			if (!cmtWedlet || !(cmtWedlet instanceof BoxCommentWedlet) || !(cmtWedlet.element instanceof BoxScComment)) showLostComment(cmtPending);
			else {
				const boxCmt = cmtWedlet.element;
				if (cmtPending.offs === -1) {
					boxCmt.addCmt(cmtPending.txt);
				} else {
					const cmt = boxCmt.getCmt(cmtPending.offs);
					if (!cmt) {
						boxCmt.addCmt(cmtPending.txt);
						//TODO ajout warning commentaire édité non retrouvé.
					} else {
						//TODO check cmtPending.dt et warning si dt !=. Quid si un cmt a été supprimé : offset faux.
						boxCmt.editCmt(cmt, cmtPending.txt);
					}
				}
			}
		} else {
			const insAct = new InsertScComment();
			const parent = WEDLET.findWedlet(wedMgr.rootWedlet, XA.up(cmtPending.xa), {forceFetch: true, lastAncestorIfNone: true});
			if (!parent || !isWedletSingleElt(parent)) showLostComment(cmtPending);
			else {
				const ctx = new WedletActionCtx(parent.element);
				if (insAct.isAvailable(ctx)) insAct.execute(ctx, null, cmtPending.txt);
			}
		}
	}
}

function showLostComment(cmt: JLDScCommentPending) {
	//FIXME show lost cmt
	console.log("Last comment lost:", cmt);
}

export class InsertScComment extends WedEditAction<IWedletActionCtx> {
	_label = "Ajouter un commentaire";
	_icon = "/@skin@/edit/wed/comment.svg";
	_group = "insObj";

	isVisible(ctx: IWedletActionCtx): boolean {
		const wedlet = ctx.wedlet as IWedletSingleElt;
		if (wedlet.isVirtual() || !(typeof wedlet.xaPart === "number")) return false;
		if (!wedlet.element || wedlet.element.getAttribute("sc-comments") === 'no') return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: IWedletActionCtx, ev?: Event, text?: string) {
		const wedlet = ctx.wedlet as IWedletSingleElt;
		const childrenElt = WEDLET.findChildrenEltForComment((wedlet.model as BoxModel).childrenEltsNaturalOrder);
		const slot = childrenElt ? childrenElt.wedSlotName : null;
		const model = ctx.wedMgr.wedModel.findModelForVirtual(ENodeType.comment, null, null, null, wedlet, scCommentSelector);
		const cmtWedlet = model.createWedlet(wedlet) as BoxCommentWedlet;
		if (text) cmtWedlet.initText = text;
		const parent = wedlet.elementHost;
		cmtWedlet.insertElement(parent, parent.firstElementChild, slot);
		await cmtWedlet.bindAsVirtual();
		if (text) cmtWedlet.initText = null;
		if (wedlet.element.onChildWedletsChange) wedlet.element.onChildWedletsChange();
	}
}

const scCommentSelector: IWedSelector = function (this: void, wedModel: IWedletModel, node: IJmlNode | string, ctx: WedMgr | IWedlet, params: any): number {
	return wedModel instanceof BoxCommentModel ? WedModel.SELECTOR_PERFECT_MATCH : WedModel.SELECTOR_REJECT;
};